import { EnumDataTypes } from "../models/constants/enumDataTypes";
import { DealQuestion } from "../models/deal-question";
import { DealValue } from "../models/deal-value";
import { ModelValue } from "../models/model-value";

/**
 * Retrieves the value from a DealValue object based on its data type.
 * 
 * When config is provided and the data type is numeric, the returned value
 * is selected based on config.caState:
 *   1        → always return nVal  (original, unadjusted value)
 *  -1        → always return nValCaAdj  (adjustment / difference only)
 *   0 / null → return nValCa when non-null, otherwise fall back to nVal
 * 
 * When config is null (default) the original behaviour is preserved exactly —
 * nVal is always returned for numeric types.
 * 
 * @param dv      The DealValue object to extract the value from.
 * @param config  Optional DealQuestion carrying caState for numeric selection.
 * @returns The value as a string, number, Date, or null if not available.
 */
export function dealValueGetValue(
  dv: DealValue | null | undefined,
  config: DealQuestion | null = null
): string | number | Date | null {

  if (dv && dv.dataTypeCode) {

    switch (dv.dataTypeCode) {
      case EnumDataTypes.Number:
        return resolveNumericValue(dv, config);
      case EnumDataTypes.String:
        return dv.sVal ?? null;
      case EnumDataTypes.DateTime:
        return dv.dtVal ?? null;
      case EnumDataTypes.Guid:
        return dv.sVal ?? null;
      default:
        return null;
    }
  }
  else
    return null;
}

/**
 * Resolves the correct numeric value from a DealValue based on caState.
 * Kept as a private module-level helper to avoid bloating dealValueGetValue.
 * 
 * caState rules:
 *   1        → nVal only when we have a non null nValCaAdj (original, unadjusted)
 *  -1        → nValCaAdj  (adjustment / difference only)
 *   0 / null → nValCa when non-null, otherwise nVal  (adjusted-first default)
 * 
 * @param dv      The DealValue record (guaranteed non-null at call site)
 * @param config  The DealQuestion config carrying caState, or null
 * @returns       The chosen numeric value, or null
 */
export function resolveNumericValue(
  dv: DealValue
  , config: DealQuestion | null
): number | null {

  // No config
  if (!config) {
    return dv.nVal ?? null;
  }

  switch (config.caState) {
    case 1:
      // Show nVal only when nValCa exists, otherwise null
      return (dv.nValCa === null || dv.nValCa === undefined) ? null : (dv.nVal ?? null);
    case -1:
      return dv.nValCaAdj ?? null;
    default:
      // Default (0 / null): prefer nValCa when present, fall back to nVal
      return dv.nValCa != null ? dv.nValCa : (dv.nVal ?? null);
  }
}

export function isDisplayText(config: DealQuestion | ModelValue): boolean {

  if (config.showToUser === true
    && (config.varName?.length ?? 0) < 1
    && (config.varNameVirt?.length ?? 0) < 1)
    return true;

  return false;
}

/**
 * Formats a template string by replacing tokens with deal values
 * and formatting the main value.
 * 
 * - Replaces [key] tokens with values from the provided map.
 * - Replaces {value} or {value:format} with the formatted value.
 * 
 * @param template The template string containing tokens to replace.
 * @param value The main value to format and insert.
 * @param values A map of key to DealValue[] for [key] token replacement.
 * @param config The DealQuestion or ModelValue configuration for formatting.
 * @param locale The locale to use for formatting (default is browser locale).
 * @returns The formatted string.
 */
export function formatDealValueString(
  template: string,
  value: any,
  values: Map<string, DealValue[]> | null,
  config: DealQuestion | ModelValue | null,
  locale: string = navigator.language
): string {
  let formattedString = template;

  // Always preserve original for {time-seg}
  const rawValue = value;

  // Step 1: Replace [key] tokens (e.g., [currency-symbol])
  formattedString = formattedString.replace(/\[([\w-]+)]/g, (match, key) => {
    if (values) {
      const dealValues = values.get(key);
      if (dealValues?.length) {
        const dealValue = dealValueGetValue(dealValues[0]);
        return dealValue != null ? dealValue.toString() : "";
      }
    }
    return "";
  });

  // Step 2: Replace {value} or {value:format} — only if value is not null/empty
  if (value != null && value !== "") {
    formattedString = formattedString.replace(/{value(?::([0-9.#]+))?}/g, (match, format) => {
      if (!config)
        return value?.toString() ?? "";

      switch (config.dataTypeCode) {
        case EnumDataTypes.Number:
          return formatDealValueNumber(value, format, locale) ?? "";
        case EnumDataTypes.DateTime:
          return formatDealValueDate(value, locale) ?? "";
        default:
          return value?.toString() ?? "";
      }
    });
  }
  else {
    // If value is null and config exists, optionally use default
    if (config?.defaultValue != null) {
      formattedString = config.defaultValue.toString();
    }
  }

  // Step 3: Always replace {time-seg}, even if value is null/empty
  formattedString = formattedString.replace(/{time-seg}/g, () => {
    return rawValue != null && rawValue !== "" ? rawValue.toString() : "";
  });
  
  return formattedString.trim();
}


/**
 * Formats a numeric value according to the specified format and locale.
 * @param value The number to format.
 * @param format The format string (e.g., "0.00" or "0.##").
 * @param locale The locale to use for formatting.
 * @returns The formatted number as a string.
 */
export function formatDealValueNumber(value: number
  , format: string | undefined
  , locale: string): string {

  if (!format)
    return value.toLocaleString(locale);

  const match = format.match(/^0+(\.(#*|0+))?$/);
  if (!match)
    return value.toLocaleString(locale);

  const minFractionDigits = match[2]?.replace(/#/g, "").length || 0;
  const maxFractionDigits = match[2]?.length || 0;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits
  }).format(value);
}

/**
 * Formats a Date value according to the specified locale.
 * @param value The Date to format.
 * @param locale The locale to use for formatting.
 * @returns The formatted date string.
 */
export function formatDealValueDate(value: Date, locale: string): string {

  //const options: Intl.DateTimeFormatOptions = {
  //  weekday: 'long',
  //  year: 'numeric',
  //  month: 'long',
  //  day: 'numeric',
  //  hour: '2-digit',
  //  minute: '2-digit',
  //  second: '2-digit'
  //};

  // return new Intl.DateTimeFormat(locale, options).format(value);

  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(value);
}

/**
 * Formats a value using a template string and token replacements.
 * - Replaces {token} with the corresponding value from the replacements object.
 * - Replaces [value] with the provided value.
 * @param value The value to insert into the template.
 * @param format The template string containing tokens.
 * @param replacements An object mapping token names to replacement values.
 * @returns The formatted string.
 */
export function isgFormatDealValue(value: number
  , format: string
  , replacements: Record<string, string>): string {

  if (value == null)
    return '';

  const tokenReplacement = format.replace(/\{(\w+)\}/g, (match, token) => {
    return replacements[token] ?? match;
  })

  const result = tokenReplacement.replace('[value]', value.toString());

  return result;
}

export function isgreplaceNthOccurrence(str: string, search: string, replace: string, n: number): string {
  let i = 0;
  return str.replace(new RegExp(search, "g"), (match) => {
    i++;
    return i === n ? replace : match;
  });
}

export function getDateValue(val: any): Date | null {

  // Handle null/undefined/empty - no logging needed
  if (val === null || val === undefined) {
    return null;
  }

  if (typeof val === 'string' && val.trim() === '') {
    return null;
  }

  try {
    const dateObject = new Date(val);

    // Check if the date is actually valid
    if (isNaN(dateObject.getTime())) {
      console.warn(`Failed to parse date value: "${val}" (type: ${typeof val})`);
      return null;
    }

    return dateObject;
  } catch (error) {
    console.error(`Error parsing date value: "${val}"`, error);
    return null;
  }
}
/**
 * Return Key by passing Value (Ex. Getting ModelValueid by passing Varname)
 * @param map
 * @param prop
 * @param value
 * @returns
 */
export function getKeysByValueProperty<T>(
  map: Map<string, T>,
  prop: keyof T,
  value: any
): string[] {

  return Array.from(map.entries())
    .filter(([_, obj]) => obj?.[prop] === value)
    .map(([key]) => key);
}
/**
 * Normalizes values for dependency comparison to handle Yes/No/1/0 equivalence.
 * Handles case-insensitive matching and treats null/undefined/empty as false.
 * 
 * True equivalents:  1, 'yes', 'Yes', 'YES', 'true', 'True', 'on', 'On'
 * False equivalents: 0, null, undefined, '', 'no', 'No', 'NO', 'false', 'False', 'off', 'Off'
 * 
 * @param value1 First value to compare
 * @param value2 Second value to compare
 * @returns true if values are equivalent
 */
export function areValuesEquivalent(
  value1: string,
  value2: string
): boolean {

  // Direct match
  if (value1 === value2) return true;

  const normalize = (val: string): string =>
    val?.toLowerCase?.().trim() ?? '';

  const v1 = normalize(value1);
  const v2 = normalize(value2);

  // Match after normalization
  if (v1 === v2) return true;

  const falseValues = new Set(['', '0', 'no', 'false', 'off']);
  const trueValues = new Set(['1', 'yes', 'true', 'on']);

  return (
    (falseValues.has(v1) && falseValues.has(v2)) ||
    (trueValues.has(v1) && trueValues.has(v2))
  );
}
